import { useState } from 'react';
import { Code2, Maximize2, Workflow, type LucideIcon } from 'lucide-react';
import { useMermaidSvg } from '../../hooks/useMermaidSvg';
import { cn } from '../../lib/utils';
import {
  ChatCodeBlockShell,
  CodeBlockShell,
  CodeText,
  PROSE_CODE_TEXT,
  chatCodeText,
} from './markdown/code-blocks';
import type { MarkdownSize } from './markdown/sizes';
import { MermaidSvg } from './MermaidSvg';
import { MermaidZoomDialog } from './MermaidZoomDialog';

type View = 'diagram' | 'code';

/** Padding around the rendered diagram, matched to each size's code padding. */
const DIAGRAM_PAD: Record<MarkdownSize, string> = { sm: 'p-3', xs: 'p-2', prose: 'p-4' };

export interface MermaidBlockProps {
  code: string;
  /** Density of the surrounding chrome. Matches the markdown sizes. */
  size?: MarkdownSize;
  className?: string;
}

/**
 * A ```mermaid fence rendered as a diagram, with a toggle back to the source.
 *
 * The diagram is the default view — that is what the author meant to show —
 * and the toggle is there because agent output is code as often as it is a
 * picture, and readers want to copy or check the source. Source that does not
 * compile falls back to the text rather than to an error card: while an agent
 * streams a fence, the partial source is invalid for as long as it takes to
 * finish, and flashing a red error box on every token is worse than showing
 * the text that is arriving.
 *
 * Chrome comes from the shared code-block shells so a diagram and the code
 * fence beside it stay on the same spacing scale — which also means a diagram
 * of any size is squeezed into a chat column, so the expand control opens it
 * full-screen with zoom (`MermaidZoomDialog`).
 */
export function MermaidBlock({ code, size = 'sm', className }: MermaidBlockProps) {
  const [view, setView] = useState<View>('diagram');
  const [zoomOpen, setZoomOpen] = useState(false);
  const { svg, error } = useMermaidSvg(code);
  // Fall back to the source only when there is no diagram to show at all. A
  // diagram that rendered earlier in the stream stays up while a later,
  // still-partial chunk fails to parse.
  const diagramUnavailable = error !== null && svg === null;
  const showCode = view === 'code' || diagramUnavailable;

  const body = showCode ? (
    <>
      {/* Docs are authored, not streamed: a fence that never compiles is a bug
          in the page, so surface mermaid's message. Chat keeps the quiet
          fallback, where a failure is expected and usually transient. */}
      {size === 'prose' && error !== null && (
        <p
          className="border-b border-border/50 px-4 py-2 font-mono text-[11px] leading-snug whitespace-pre-wrap text-status-error"
          data-testid="mermaid-error"
        >
          {error}
        </p>
      )}
      <CodeText code={code} className={size === 'prose' ? PROSE_CODE_TEXT : chatCodeText(size)} />
    </>
  ) : svg ? (
    <MermaidSvg svg={svg} className={cn('items-center bg-card', DIAGRAM_PAD[size])} />
  ) : (
    <div
      className={cn(
        'flex min-h-24 items-center justify-center bg-card text-muted-foreground',
        DIAGRAM_PAD[size],
        size === 'prose' ? 'text-[11px]' : 'text-3xs',
      )}
    >
      Rendering diagram…
    </div>
  );

  const actions = (
    <>
      {/* Expanding is about the picture, so it is offered whenever there is a
          picture to show — including from the source view, where it is the
          quickest way back to a readable diagram. */}
      <IconButton
        onClick={() => setZoomOpen(true)}
        label="Expand diagram"
        icon={Maximize2}
        disabled={svg === null}
      />
      <ViewToggle
        view={showCode ? 'code' : 'diagram'}
        onChange={setView}
        diagramUnavailable={diagramUnavailable}
      />
    </>
  );

  const zoomDialog = <MermaidZoomDialog svg={svg} open={zoomOpen} onOpenChange={setZoomOpen} />;

  if (size === 'prose') {
    return (
      <CodeBlockShell
        code={code}
        lang="mermaid"
        actions={actions}
        className={className}
        testId="mermaid-block"
      >
        {body}
        {zoomDialog}
      </CodeBlockShell>
    );
  }
  return (
    <ChatCodeBlockShell
      size={size}
      code={code}
      lang="mermaid"
      actions={actions}
      className={className}
      testId="mermaid-block"
    >
      {body}
      {zoomDialog}
    </ChatCodeBlockShell>
  );
}

function ViewToggle({
  view,
  onChange,
  diagramUnavailable,
}: {
  view: View;
  onChange: (view: View) => void;
  diagramUnavailable: boolean;
}) {
  return (
    <div
      className="flex items-center gap-0.5 rounded-md border border-border/50 bg-muted/50 p-0.5"
      role="group"
      aria-label="Mermaid view"
    >
      <IconButton
        pressed={view === 'diagram'}
        onClick={() => onChange('diagram')}
        label="Show diagram"
        icon={Workflow}
        disabled={diagramUnavailable}
      />
      <IconButton
        pressed={view === 'code'}
        onClick={() => onChange('code')}
        label="Show source"
        icon={Code2}
      />
    </div>
  );
}

/**
 * One control in the block's header. `pressed` marks a view toggle as the
 * selected one; plain actions (expand) leave it off, so they do not claim to
 * be a toggle to a screen reader.
 */
function IconButton({
  pressed,
  onClick,
  label,
  icon: Icon,
  disabled,
}: {
  pressed?: boolean;
  onClick: () => void;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      title={label}
      className={cn(
        'rounded p-1 text-muted-foreground transition-colors',
        'hover:text-foreground',
        'disabled:pointer-events-none disabled:opacity-40',
        pressed && 'bg-background text-foreground shadow-sm',
      )}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}
